import { createStyles, Group, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';
import { trackAnonymousEvent } from '@repo/common/analytics';
import { Button, Select, Text, TextInput, Title } from '@repo/foundations';
import { isEmpty } from 'lodash-es';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useMaybeAuthUser, useUpdateUser } from '../../api';
import { useUserProfileImage } from '../../api/hooks/user/useUserProfileImage';
import { AvatarUpload } from '../ImageUpload/AvatarUpload';
import { AuthCard } from './AuthCard';
import { AuthFooter } from './AuthLayouts/AuthFooter';
import { AuthLayoutBase } from './AuthLayouts/AuthLayoutBase';
import {
	anonymousId,
	EMAIL_VERIFICATION_LOCAL_STORAGE_KEY,
	referrer,
} from './AuthStepEmailComponent.constants';
import { ROLE_OPTIONS, USE_SECODA_FOR_OPTIONS } from './consts';

const useStyles = createStyles((theme) => ({
	page: {
		display: 'flex',
		justifyContent: 'center',
		backgroundColor: theme.other.getColor('surface/secondary/default'),
		height: '100vh',
		alignItems: 'center',
	},
	card: {
		width: 500,
		marginTop: 50,
	},
}));

const NAME_REGEX =
	/^[a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð ,.'-]+$/u;

interface UserProfileType {
	firstName?: string;
	lastName?: string;
	roleName?: string;
	useReason?: string;
	profilePicture?: File | null;
}

export function AuthStepProfile() {
	const { theme } = useStyles();
	const navigate = useNavigate();
	const { user } = useMaybeAuthUser();
	const { mutateAsync } = useUpdateUser({});
	const { mutateAsync: uploadImage } = useUserProfileImage();

	const form = useForm<UserProfileType>({
		initialValues: {
			firstName: user?.first_name,
			lastName: user?.last_name,
			roleName: user?.role_name,
			useReason: user?.use_reason,
		},

		validate: {
			firstName: (value) =>
				!isEmpty(value) && value?.match(NAME_REGEX) ? null : 'Invalid name',
			lastName: (value) =>
				!isEmpty(value) && value?.match(NAME_REGEX) ? null : 'Invalid name',
			// Always return null for profilePicture, as it's optional.
			profilePicture: () => null,
		},
	});

	const handleSubmit = async (values: UserProfileType) => {
		trackAnonymousEvent(
			'auth/verify',
			{
				path: window.location.pathname,
				anonymous_id: anonymousId(),
				referrer: referrer(),
			},
			localStorage.getItem(EMAIL_VERIFICATION_LOCAL_STORAGE_KEY) ?? ''
		);

		if (user) {
			const data = {
				first_name: values.firstName,
				last_name: values.lastName,
				use_reason: values.useReason,
				role_name: values.roleName,
				id: user.id,
			};

			await mutateAsync({
				data,
			});

			if (values.profilePicture) {
				await uploadImage({
					file: values.profilePicture,
				});
			}

			if (user.workspace_id) {
				// Skip workspace creation, if user already has one
				navigate('/onboarding/confirm');
				return;
			}

			navigate('/onboarding/workspace');
		}
	};

	const handleFile = useCallback(
		(file: File) => {
			form.setFieldValue('profilePicture', file);
		},
		[form]
	);

	if (!user) {
		return null;
	}

	return (
		<AuthLayoutBase>
			<AuthCard p="2xl">
				<Stack spacing="xl">
					<Stack spacing="3xs">
						<Title>Tell us about yourself</Title>
						<Text color="text/secondary/default">
							These details will help us give you a better experience.
						</Text>
					</Stack>
					<form
						style={{
							display: 'flex',
							flexDirection: 'column',
							gap: theme.spacing.md,
						}}
						onSubmit={form.onSubmit(handleSubmit)}
					>
						<Group position="left">
							<AvatarUpload
								callback={handleFile}
								label="Profile photo"
								placeholder="/images/auth-image/profile-placeholder.webp"
								user={user}
							/>
						</Group>
						<Group spacing="sm" grow>
							<TextInput
								autoFocus
								label="First name"
								{...form.getInputProps('firstName')}
							/>
							<TextInput
								label="Last name"
								{...form.getInputProps('lastName')}
							/>
						</Group>
						<Select
							data={ROLE_OPTIONS}
							label="Role (Optional)"
							placeholder="Select"
							{...form.getInputProps('roleName')}
						/>
						<Select
							data={USE_SECODA_FOR_OPTIONS}
							label="What are you hoping to use Secoda for? (Optional)"
							placeholder="Select"
							styles={{
								item: {
									whiteSpace: 'normal',
									wordBreak: 'break-word',
								},
							}}
							{...form.getInputProps('useReason')}
						/>
						<AuthFooter>
							<Group position="right" py="md" px="2xl">
								<Group spacing="xs" position="right">
									<Button
										disabled={!form.isValid}
										type="submit"
										variant="primary"
									>
										Continue
									</Button>
								</Group>
							</Group>
						</AuthFooter>
					</form>
				</Stack>
			</AuthCard>
		</AuthLayoutBase>
	);
}
